import L from "leaflet";
import type { DrawMode, LatLngTuple } from "../core/options";
import { distanceMeters } from "../geo/geodesy";
import { nearestSegmentIndex } from "../geo/snap";
import { drawColor, drawInvalidColor } from "./host";
import type { Tool, ToolHost } from "./host";
import { movePoints } from "./LineOps";
import { VertexHandles } from "./VertexHandles";

const doubleClickToleranceMeters = 0.5;

export class LineTool implements Tool {
  readonly mode: DrawMode = "polyline";

  protected readonly host: ToolHost;
  protected readonly closed: boolean;
  protected path: L.Polyline<GeoJSON.LineString | GeoJSON.MultiLineString | GeoJSON.Polygon | GeoJSON.MultiPolygon>;
  protected rubberBand: L.Polyline;
  protected handles: VertexHandles;
  protected editable: boolean;
  private moveStart: { origin: L.LatLng; points: LatLngTuple[] } | null = null;
  private lastClickAt = 0;

  constructor(host: ToolHost, closed = false) {
    this.host = host;
    this.closed = closed;
    this.editable = host.editable();
    this.path = this.createPath();
    this.rubberBand = L.polyline([], {
      pane: host.pane,
      renderer: host.renderer,
      interactive: false,
      className: "fxmap-draw-rubber-band",
      color: drawColor,
      weight: 2,
      dashArray: "6 6"
    }).addTo(host.host.map);
    this.handles = new VertexHandles(host, closed);
    this.attachPathHandlers();
    host.host.map.on("mousemove", this.onSegmentMove, this);
    host.host.map.on("mouseup", this.onSegmentUp, this);
  }

  protected createPath(): L.Polyline<GeoJSON.LineString | GeoJSON.MultiLineString | GeoJSON.Polygon | GeoJSON.MultiPolygon> {
    return L.polyline([], {
      pane: this.host.pane,
      renderer: this.host.renderer,
      className: "fxmap-draw-path",
      color: drawColor,
      weight: 4,
      opacity: 0.95,
      interactive: true,
      bubblingMouseEvents: false
    }).addTo(this.host.host.map);
  }

  render(points: LatLngTuple[], valid: boolean): void {
    this.path.setLatLngs(points);
    this.path.setStyle({ dashArray: valid ? undefined : "8 6", color: valid ? drawColor : drawInvalidColor });

    if (this.host.phase() === "editing" || !this.editable) this.rubberBand.setLatLngs([]);

    this.handles.render(points);
  }

  onMapClick(point: LatLngTuple): void {
    if (!this.editable) return;
    if (this.host.phase() === "editing" && !this.appendsWhileEditing()) return;

    this.lastClickAt = Date.now();
    this.host.commit([...this.host.points(), point]);
  }

  protected appendsWhileEditing(): boolean {
    return !this.closed;
  }

  onMapMouseMove(latlng: LatLngTuple): void {
    if (!this.editable || this.host.phase() !== "drawing") return;

    const points = this.host.points();

    if (points.length === 0) {
      this.rubberBand.setLatLngs([]);
      this.host.hideSegment();
      return;
    }

    const last = points[points.length - 1];
    const band: LatLngTuple[] = this.closed && points.length > 1 ? [last, latlng, points[0]] : [last, latlng];

    this.rubberBand.setLatLngs(band);
    this.host.showSegment(latlng, distanceMeters(last, latlng));
  }

  onDoubleClick(): void {
    if (!this.editable || this.host.phase() !== "drawing") return;

    const points = this.host.points();

    if (points.length >= 2 && Date.now() - this.lastClickAt < 600) {
      const last = points[points.length - 1];
      const previous = points[points.length - 2];

      if (distanceMeters(last, previous) < doubleClickToleranceMeters) this.host.replaceWithoutHistory(points.slice(0, -1));
    }

    this.host.finish();
  }

  removeLast(): void {
    if (!this.editable) return;

    const points = this.host.points();

    if (points.length === 0) return;

    this.host.commit(points.slice(0, -1));
  }

  deleteSelectedVertex(): void {
    if (!this.editable) return;

    this.handles.removeSelected();
  }

  finishGesture(): void {
    this.rubberBand.setLatLngs([]);
    this.host.hideSegment();
  }

  setEditable(on: boolean): void {
    this.editable = on;
    this.handles.setEnabled(on);

    if (!on) this.finishGesture();
  }

  dispose(): void {
    this.host.host.map.off("mousemove", this.onSegmentMove, this);
    this.host.host.map.off("mouseup", this.onSegmentUp, this);
    this.handles.dispose();
    this.path.remove();
    this.rubberBand.remove();
    this.moveStart = null;
  }

  private attachPathHandlers(): void {
    this.path.on("mousedown", event => {
      const original = event.originalEvent as MouseEvent;

      if (!this.editable || !original.altKey) return;

      L.DomEvent.stop(event);
      this.host.host.map.dragging.disable();
      this.moveStart = { origin: event.latlng, points: this.host.points() };
    });

    this.path.on("click", event => {
      const original = event.originalEvent as MouseEvent;

      if (this.moveStart !== null) return;

      L.DomEvent.stopPropagation(event);

      if (!this.editable) return;

      if (original.ctrlKey || original.metaKey) {
        const points = this.host.points();
        const hit = nearestSegmentIndex(points, [event.latlng.lat, event.latlng.lng], this.host.host.map, this.closed);

        if (hit !== null && !this.closed) this.host.splitAt(hit.index, hit.point);

        return;
      }

      if (this.host.phase() === "drawing" && this.appendsWhileEditing()) this.onMapClick([event.latlng.lat, event.latlng.lng]);
    });

    this.path.on("dblclick", event => {
      L.DomEvent.stop(event);
      this.onDoubleClick();
    });
  }

  private onSegmentMove(event: L.LeafletMouseEvent): void {
    if (this.moveStart === null) return;

    const deltaLat = event.latlng.lat - this.moveStart.origin.lat;
    const deltaLng = event.latlng.lng - this.moveStart.origin.lng;

    this.host.preview(movePoints(this.moveStart.points, deltaLat, deltaLng));
  }

  private onSegmentUp(event: L.LeafletMouseEvent): void {
    if (this.moveStart === null) return;

    const deltaLat = event.latlng.lat - this.moveStart.origin.lat;
    const deltaLng = event.latlng.lng - this.moveStart.origin.lng;
    const moved = movePoints(this.moveStart.points, deltaLat, deltaLng);

    this.moveStart = null;
    this.host.host.map.dragging.enable();
    this.host.commit(moved);
  }
}
